<script setup lang="ts">
  import { computed, onMounted, reactive, ref } from 'vue';
  import { FieldSearchBar } from '@/shared/ui';
  import { useCustomToast } from '@/shared/lib';
  import { SETTINGS_SECTIONS, SETTINGS_ORDER_OPTIONS } from '../model/settings-config';
  import type { SettingsEntityKey } from '../model/settings.types';
  import {
    useIndexingManagement,
    useProtectionMeansManagement,
    useRecordOriginManagement,
    useRecordStageManagement,
    useRecordTypeManagement,
  } from '../lib/settings-management';
  import { useSettingsFieldModals, useRecordOriginModals } from '../lib/settings-modals';
  import {
    buildSettingsListQuery,
    settingsListQuerySignature,
    type SettingsListQuery,
  } from '../lib/settings-list-query';
  import { SETTINGS_TOAST_MESSAGES } from '../lib/settings-messages';
  import SettingsSectionCard from './SettingsSectionCard.vue';

  const SEARCH_DEBOUNCE_MS = 500;

  const [indexingSection, recordOriginSection, recordTypeSection, protectionMeansSection, recordStageSection] =
    SETTINGS_SECTIONS;

  const indexing = useIndexingManagement();
  const recordType = useRecordTypeManagement();
  const protectionMeans = useProtectionMeansManagement();
  const recordStage = useRecordStageManagement();
  const recordOrigin = useRecordOriginManagement();

  const indexingModals = useSettingsFieldModals(indexingSection, indexing);
  const recordTypeModals = useSettingsFieldModals(recordTypeSection, recordType);
  const protectionMeansModals = useSettingsFieldModals(protectionMeansSection, protectionMeans);
  const recordStageModals = useSettingsFieldModals(recordStageSection, recordStage);
  const recordOriginModals = useRecordOriginModals(
    recordOrigin,
    computed(() => recordType.items.value),
    computed(() => protectionMeans.items.value)
  );

  const toast = useCustomToast();

  const selectedSectionKey = ref<SettingsEntityKey>(SETTINGS_SECTIONS[0].key);
  const searchTerm = ref('');
  const searchFields = SETTINGS_SECTIONS.map((section) => ({ id: section.key, name: section.title }));

  const listReaders: Record<SettingsEntityKey, (query: SettingsListQuery) => Promise<boolean>> = {
    indexing: (query) => indexing.fetchAll(query),
    recordOrigin: (query) => recordOrigin.fetchAll(query),
    recordType: (query) => recordType.fetchAll(query),
    protectionMeans: (query) => protectionMeans.fetchAll(query),
    recordStage: (query) => recordStage.fetchAll(query),
  };

  const sortSelection = reactive<Record<SettingsEntityKey, { sort: string; order: string }>>({
    indexing: { sort: '', order: '' },
    recordOrigin: { sort: '', order: '' },
    recordType: { sort: '', order: '' },
    protectionMeans: { sort: '', order: '' },
    recordStage: { sort: '', order: '' },
  });

  const appliedSignatures: Partial<Record<SettingsEntityKey, string>> = {};
  let latestSync = 0;

  const hasActiveSearch = computed(() => searchTerm.value.trim().length > 0);

  function queryFor(key: SettingsEntityKey): SettingsListQuery {
    const term = key === selectedSectionKey.value ? searchTerm.value : '';

    return buildSettingsListQuery(term, sortSelection[key].sort, sortSelection[key].order);
  }

  async function syncSections(): Promise<boolean> {
    const sync = (latestSync += 1);

    const results = await Promise.all(
      SETTINGS_SECTIONS.map(async ({ key }) => {
        const query = queryFor(key);
        const signature = settingsListQuerySignature(query);
        if (appliedSignatures[key] === signature) return true;

        const succeeded = await listReaders[key](query);
        // Only the newest synchronisation may record what is in force, and only it may complain:
        // an older one finishing later would record a query the screen no longer shows. A failed
        // read records nothing, so it is asked again on the next change.
        if (sync !== latestSync) return true;
        if (succeeded) appliedSignatures[key] = signature;

        return succeeded;
      })
    );

    return results.every(Boolean);
  }

  const handleSectionChange = async (id: string) => {
    selectedSectionKey.value = id as SettingsEntityKey;
    await syncSections();
  };

  const handleSearch = async (term: string) => {
    searchTerm.value = term;

    if (!(await syncSections())) {
      toast.showErrorToast({ title: SETTINGS_TOAST_MESSAGES.searchError, type: 'error' });
    }
  };

  const handleSortUpdate = async (key: SettingsEntityKey, sort: string, order: string) => {
    sortSelection[key] = { sort, order };
    await syncSections();
  };

  function isSectionVisible(key: SettingsEntityKey): boolean {
    if (!hasActiveSearch.value) return true;
    return key === selectedSectionKey.value;
  }

  onMounted(syncSections);
</script>

<template>
  <div class="flex flex-col gap-6">
    <FieldSearchBar
      :search-field-id="selectedSectionKey"
      :search-fields="searchFields"
      independent-search-field
      :clear-search-on-field-change="false"
      :debounce-ms="SEARCH_DEBOUNCE_MS"
      @update:search-field-id="handleSectionChange"
      @search="handleSearch"
    />

    <SettingsSectionCard
      v-if="isSectionVisible('indexing')"
      :title="indexingSection.title"
      :column-label="indexingSection.columnLabel"
      :empty-message="indexingSection.emptyMessage"
      :items="indexing.items.value"
      :order-options="SETTINGS_ORDER_OPTIONS"
      :is-loading="indexing.isLoading.value"
      :is-search-active="hasActiveSearch"
      :error="indexing.readError.value"
      @add="indexingModals.openAdd"
      @edit="indexingModals.openEdit"
      @delete="indexingModals.openDelete"
      @update:sort="(sort, order) => handleSortUpdate('indexing', sort, order)"
    />

    <SettingsSectionCard
      v-if="isSectionVisible('recordOrigin')"
      :title="recordOriginSection.title"
      :column-label="recordOriginSection.columnLabel"
      :empty-message="recordOriginSection.emptyMessage"
      :items="recordOrigin.items.value"
      :order-options="SETTINGS_ORDER_OPTIONS"
      :is-loading="recordOrigin.isLoading.value"
      :is-search-active="hasActiveSearch"
      :error="recordOrigin.readError.value"
      @add="recordOriginModals.openAdd"
      @edit="recordOriginModals.openEdit"
      @delete="recordOriginModals.openDelete"
      @update:sort="(sort, order) => handleSortUpdate('recordOrigin', sort, order)"
    />

    <SettingsSectionCard
      v-if="isSectionVisible('recordType')"
      :title="recordTypeSection.title"
      :column-label="recordTypeSection.columnLabel"
      :empty-message="recordTypeSection.emptyMessage"
      :items="recordType.items.value"
      :order-options="SETTINGS_ORDER_OPTIONS"
      :is-loading="recordType.isLoading.value"
      :is-search-active="hasActiveSearch"
      :error="recordType.readError.value"
      @add="recordTypeModals.openAdd"
      @edit="recordTypeModals.openEdit"
      @delete="recordTypeModals.openDelete"
      @update:sort="(sort, order) => handleSortUpdate('recordType', sort, order)"
    />

    <SettingsSectionCard
      v-if="isSectionVisible('protectionMeans')"
      :title="protectionMeansSection.title"
      :column-label="protectionMeansSection.columnLabel"
      :empty-message="protectionMeansSection.emptyMessage"
      :items="protectionMeans.items.value"
      :order-options="SETTINGS_ORDER_OPTIONS"
      :is-loading="protectionMeans.isLoading.value"
      :is-search-active="hasActiveSearch"
      :error="protectionMeans.readError.value"
      @add="protectionMeansModals.openAdd"
      @edit="protectionMeansModals.openEdit"
      @delete="protectionMeansModals.openDelete"
      @update:sort="(sort, order) => handleSortUpdate('protectionMeans', sort, order)"
    />

    <SettingsSectionCard
      v-if="isSectionVisible('recordStage')"
      :title="recordStageSection.title"
      :column-label="recordStageSection.columnLabel"
      :empty-message="recordStageSection.emptyMessage"
      :items="recordStage.items.value"
      :order-options="SETTINGS_ORDER_OPTIONS"
      :is-loading="recordStage.isLoading.value"
      :is-search-active="hasActiveSearch"
      :error="recordStage.readError.value"
      @add="recordStageModals.openAdd"
      @edit="recordStageModals.openEdit"
      @delete="recordStageModals.openDelete"
      @update:sort="(sort, order) => handleSortUpdate('recordStage', sort, order)"
    />
  </div>
</template>
